import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Upload, Trash2, Star, StarOff, Eye, EyeOff, Image, Video, GripVertical, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Media = () => {
  const [media, setMedia] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [fileType, setFileType] = useState('image');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filterProduct, setFilterProduct] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [editingMedia, setEditingMedia] = useState(null);
  const [editCaption, setEditCaption] = useState('');
  const [editAltText, setEditAltText] = useState('');

  useEffect(() => {
    fetchMedia();
    fetchProducts();
  }, [filterProduct, filterType]);

  const fetchMedia = async () => {
  try {
    let url = `${BACKEND_URL}/api/admin/media?limit=200`;
    if (filterProduct && filterProduct !== 'all') url += `&product_id=${filterProduct}`;  // ✅ Added check
    if (filterType && filterType !== 'all') url += `&file_type=${filterType}`;  // ✅ Added check
    
    const response = await fetch(url, { credentials: 'include' });
    if (response.ok) {
      const data = await response.json();
      setMedia(data.media || []);
    }
  } catch (error) {
    console.error('Failed to fetch media:', error);
    toast.error('Failed to load media');
  } finally {
    setLoading(false);
  }
};;

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSelectedFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  const handleUpload = async () => {
    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }
    
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    const newProgress = {};
    
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        newProgress[file.name] = 0;
        setUploadProgress({...newProgress});
        
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(
          `${BACKEND_URL}/api/admin/media/upload?product_id=${selectedProduct}&file_type=${fileType}`,
          {
            method: 'POST',
            credentials: 'include',
            body: formData
          }
        );

        if (response.ok) {
          newProgress[file.name] = 100;
          setUploadProgress({...newProgress});
        } else {
          const error = await response.json();
          toast.error(`Failed to upload ${file.name}: ${error.detail || 'Unknown error'}`);
        }
      }

      toast.success(`Successfully uploaded ${selectedFiles.length} file(s)`);
      setShowUploadModal(false);
      setSelectedFiles([]);
      setUploadProgress({});
      fetchMedia();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSetPrimary = async (mediaId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/media/${mediaId}/set-primary`,
        {
          method: 'PUT',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Primary media set successfully');
        fetchMedia();
      } else {
        toast.error('Failed to set primary media');
      }
    } catch (error) {
      console.error('Error setting primary:', error);
      toast.error('Failed to set primary media');
    }
  };

  const handleToggleStatus = async (mediaId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const response = await fetch(
        `${BACKEND_URL}/api/admin/media/${mediaId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ status: newStatus })
        }
      );

      if (response.ok) {
        toast.success(`Media ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchMedia();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (mediaId, permanent = false) => {
    if (!window.confirm(permanent ? 'Permanently delete this media? This cannot be undone.' : 'Delete this media?')) {
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/media/${mediaId}?permanent=${permanent}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Media deleted successfully');
        fetchMedia();
      } else {
        toast.error('Failed to delete media');
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete media');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMedia.length === 0) {
      toast.error('No media selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedMedia.length} selected media items?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/media/bulk-delete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(selectedMedia)
        }
      );

      if (response.ok) {
        toast.success('Selected media deleted successfully');
        setSelectedMedia([]);
        fetchMedia();
      } else {
        toast.error('Failed to delete selected media');
      }
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error('Failed to delete selected media');
    }
  };

  const handleEditMedia = (mediaItem) => {
    setEditingMedia(mediaItem);
    setEditCaption(mediaItem.caption || '');
    setEditAltText(mediaItem.alt_text || '');
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/media/${editingMedia.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            caption: editCaption,
            alt_text: editAltText
          })
        }
      );

      if (response.ok) {
        toast.success('Media updated successfully');
        setEditingMedia(null);
        fetchMedia();
      } else {
        toast.error('Failed to update media');
      }
    } catch (error) {
      console.error('Error updating media:', error);
      toast.error('Failed to update media');
    }
  };

  const toggleMediaSelection = (mediaId) => {
    setSelectedMedia(prev =>
      prev.includes(mediaId)
        ? prev.filter(id => id !== mediaId)
        : [...prev, mediaId]
    );
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Unknown Product';
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Media Management</h1>
            <p className="text-gray-600 mt-1">Manage product images and videos</p>
          </div>
          <Button onClick={() => setShowUploadModal(true)} className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Upload Media
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Filter by Product</Label>
              <Select value={filterProduct} onValueChange={setFilterProduct}>
                <SelectTrigger>
                  <SelectValue placeholder="All Products" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>  {/* ✅ Changed to "all" */}
                  {products.map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
            <Label>Filter by Type</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>  {/* ✅ Changed to "all" */}
                <SelectItem value="image">Images</SelectItem>
                <SelectItem value="video">Videos</SelectItem>
              </SelectContent>
            </Select>
          </div>

            <div className="flex items-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setFilterProduct('all');  // ✅ Changed from ''
                  setFilterType('all');     // ✅ Changed from ''
                }}
              >
                Clear Filters
              </Button>
              {selectedMedia.length > 0 && (
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                >
                  Delete Selected ({selectedMedia.length})
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Media Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
            <p className="mt-2 text-gray-600">Loading media...</p>
          </div>
        ) : media.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No media found</p>
            <Button onClick={() => setShowUploadModal(true)} className="mt-4">
              Upload First Media
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {media.map(item => (
              <Card key={item.id} className="overflow-hidden">
                <div className="relative aspect-square bg-gray-100">
                  <input
                    type="checkbox"
                    checked={selectedMedia.includes(item.id)}
                    onChange={() => toggleMediaSelection(item.id)}
                    className="absolute top-2 left-2 z-10 h-5 w-5 cursor-pointer"
                  />
                  {item.file_type === 'image' ? (
                    <img
                      src={item.file_url}
                      alt={item.alt_text || 'Product media'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <video
                      src={item.file_url}
                      className="w-full h-full object-cover"
                      controls
                    />
                  )}
                  {item.is_primary && (
                    <Badge className="absolute top-2 right-2 bg-yellow-500">
                      <Star className="h-3 w-3 mr-1" />
                      Primary
                    </Badge>
                  )}
                  <Badge
                    className={`absolute bottom-2 right-2 ${
                      item.status === 'active' ? 'bg-green-500' : 'bg-gray-500'
                    }`}
                  >
                    {item.status}
                  </Badge>
                </div>

                <div className="p-3">
                  <div className="flex items-center gap-1 mb-2">
                    {item.file_type === 'image' ? (
                      <Image className="h-4 w-4" />
                    ) : (
                      <Video className="h-4 w-4" />
                    )}
                    <span className="text-sm font-medium capitalize">{item.file_type}</span>
                  </div>
                  
                  <p className="text-sm text-gray-600 truncate mb-1">
                    {getProductName(item.product_id)}
                  </p>
                  
                  {item.caption && (
                    <p className="text-xs text-gray-500 truncate mb-2">{item.caption}</p>
                  )}

                  <div className="text-xs text-gray-400 mb-3">
                    {(item.file_size / 1024 / 1024).toFixed(2)} MB
                    {item.width && item.height && ` • ${item.width}×${item.height}`}
                  </div>

                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleEditMedia(item)}
                    >
                      Edit
                    </Button>
                    
                    {!item.is_primary && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSetPrimary(item.id)}
                        title="Set as primary"
                      >
                        <Star className="h-4 w-4" />
                      </Button>
                    )}
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleStatus(item.id, item.status)}
                      title={item.status === 'active' ? 'Deactivate' : 'Activate'}
                    >
                      {item.status === 'active' ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Upload Modal */}
        <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Upload Media</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Select Product *</Label>
                <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>File Type</Label>
                <Select value={fileType} onValueChange={setFileType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Image (Max 5MB)</SelectItem>
                    <SelectItem value="video">Video (Max 100MB)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center ${
                  dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg mb-2">Drag & drop files here</p>
                <p className="text-sm text-gray-500 mb-4">or</p>
                
                {/* ✅ Fixed: Input wraps button with proper label */}
                <input
                  type="file"
                  multiple
                  accept={fileType === 'image' ? 'image/*' : 'video/*'}
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Button variant="outline" type="button" asChild>
                    <span>Browse Files</span>
                  </Button>
                </label>
                
                <p className="text-xs text-gray-500 mt-4">
                  {fileType === 'image'
                    ? 'Supported: JPEG, PNG, WebP, GIF (Max 5MB each)'
                    : 'Supported: MP4, MOV, WebM (Max 100MB each)'}
                </p>
              </div>

              {selectedFiles.length > 0 && (
                <div className="space-y-2">
                  <Label>Selected Files ({selectedFiles.length})</Label>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm truncate flex-1">{file.name}</span>
                      <span className="text-xs text-gray-500 mr-2">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                      {uploadProgress[file.name] !== undefined && (
                        <span className="text-xs text-green-600">
                          {uploadProgress[file.name]}%
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedFiles(selectedFiles.filter((_, i) => i !== index))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadModal(false)} disabled={uploading}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={uploading || selectedFiles.length === 0}>
                {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={!!editingMedia} onOpenChange={() => setEditingMedia(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Media</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Caption</Label>
                <Input
                  value={editCaption}
                  onChange={(e) => setEditCaption(e.target.value)}
                  placeholder="Enter caption"
                />
              </div>

              <div>
                <Label>Alt Text</Label>
                <Input
                  value={editAltText}
                  onChange={(e) => setEditAltText(e.target.value)}
                  placeholder="Enter alt text for accessibility"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingMedia(null)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default Media;
